import React, { useEffect, useState } from 'react';
import CommonModal from '@/components/common/CommonModal';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, formatRand, GetFilterUrl } from '@/utility/helper';
import { Calendar, Hash, Eye, DollarSign, Store, Percent } from 'lucide-react';
import Pagination from '@/components/common/Pagination';
import { IPageInfo } from '@/interfaces/common.interface';
import { StatusBadge } from '@/components/common/StatusBadge';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { IEarningList, IStoreEarningListResponse } from '@/interfaces/IStoreSettlement/IStoreSettlement.interface';

import { format } from 'date-fns';

interface StoreSettlementOrdersModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storeId?: string | null;
    startDate?: string | null;
    endDate?: string | null;
}

export const StoreSettlementOrdersModal: React.FC<StoreSettlementOrdersModalProps> = ({
    open,
    onOpenChange,
    storeId,
    startDate,
    endDate,
}) => {
    const [earnings, setEarnings] = useState<IEarningList[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState<IPageInfo>({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const navigate = useRoleNavigate();

    const handleViewOrder = (orderId: string) => {
        if (!orderId) return;
        navigate(RoutesLink.ViewOrders.replace(':id', orderId));
        onOpenChange(false);
    };

    const fetchOrders = async (page = 1) => {
        if (!storeId) return;
        setLoading(true);
        try {
            const formattedStart = startDate ? format(new Date(startDate), 'yyyy-MM-dd') : undefined;
            const formattedEnd = endDate ? format(new Date(endDate), 'yyyy-MM-dd') : undefined;

            const urlParams = GetFilterUrl({
                page: page,
                limit: pageInfo.limit,
                storeId: storeId,
                ...(formattedStart ? { startDate: formattedStart } : {}),
                ...(formattedEnd ? { endDate: formattedEnd } : {}),
                isSettled: true
            });

            const response = await callApi<IStoreEarningListResponse>(
                `${API_URL}${API_ENDPOINT.storeEarningList}?${urlParams}`,
                { method: 'GET' }
            );

            const list = response.data?.earnings || [];
            setEarnings(list);
            if (response.data?.pageInfo) {
                setPageInfo(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && storeId) {
            fetchOrders(1);
        } else {
            setEarnings([]);
            setPageInfo({ page: 1, limit: 10, total: 0, totalPages: 0 });
        }
    }, [open, storeId, startDate, endDate]);

    // Calculate totals from current loaded page / list
    const totalStorePayable = earnings.reduce((acc, curr) => acc + Number(curr.storePayableAmount || 0), 0);
    const totalCommission = earnings.reduce((acc, curr) => acc + Number(curr.commissionAmount || 0), 0);

    const columns: TableColumn<IEarningList>[] = [
        {
            key: "orderId",
            header: "Order / Ref ID",
            render: (item) => {
                const orderId = item.order?.id || item.orderId;
                const displayCode = item.order?.orderId || item.orderId || item.id;
                return (
                    <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleViewOrder(orderId)}
                    >
                        <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-mono text-sm font-bold text-primary underline underline-offset-2">
                            {displayCode}
                        </span>
                    </div>
                );
            }
        },
        {
            key: "date",
            header: "Date",
            render: (item) => (
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDateTime(item.createdAt || item.order?.createdAt)}
                </div>
            )
        },
        {
            key: "subtotal",
            header: "Subtotal",
            render: (item) => (
                <div className="font-bold text-foreground text-sm">
                    {formatRand(Number(item.subtotal || item.order?.finalAmount || 0))}
                </div>
            )
        },
        {
            key: "commissionAmount",
            header: "Commission",
            render: (item) => (
                <div className="font-medium text-xs text-muted-foreground">
                    {formatRand(Number(item.commissionAmount || 0))} ({item.commissionPercentage || '0'}%)
                </div>
            )
        },
        {
            key: "storePayableAmount",
            header: "Store Earning",
            render: (item) => (
                <div className="font-extrabold text-sm text-emerald-600">
                    {formatRand(Number(item.storePayableAmount || 0))}
                </div>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <StatusBadge status={item.status || item.order?.status || 'completed'} />
            )
        }
    ];

    const actions = [
        {
            text: 'View Order',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer text-muted-foreground hover:text-primary transition-colors" />,
            onClick: (record: IEarningList) => {
                const orderId = record.order?.id || record.orderId;
                handleViewOrder(orderId);
            },
        }
    ];

    return (
        <CommonModal
            open={open}
            onOpenChange={onOpenChange}
            title="Included Settlement Orders"
            size="xl"
        >
            <div className="space-y-4 pt-4">
                <CommonTable
                    columns={columns}
                    data={earnings}
                    loading={loading}
                    emptyMessage="No orders found for this settlement period."
                    actions={actions}
                />

                {pageInfo.totalPages > 1 && (
                    <Pagination
                        currentPage={pageInfo.page}
                        totalPages={pageInfo.totalPages}
                        totalItems={pageInfo.total}
                        itemsPerPage={pageInfo.limit}
                        onPageChange={fetchOrders}
                    />
                )}

                <div className="mt-6 pt-4 border border-dashed flex justify-between items-center p-4 bg-secondary/10 rounded-xl">
                    <div className="flex gap-4 items-center">
                        <Store className="w-5 h-5 text-primary" />
                        <span className="font-semibold text-sm text-muted-foreground">
                            Total Orders: <span className="text-foreground font-bold">{pageInfo.total || earnings.length}</span>
                        </span>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                        <div className="flex items-center gap-4">
                            <span className="font-medium text-xs text-muted-foreground flex items-center gap-1">
                                <Percent className="w-3 h-3" /> Total Commission:
                            </span>
                            <span className="text-sm font-bold text-foreground">{formatRand(totalCommission)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-xs text-muted-foreground flex items-center gap-1">
                                <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Total Store Payable:
                            </span>
                            <span className="text-xl font-black text-emerald-600">{formatRand(totalStorePayable)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};
